import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export interface GlobalStats {
  total_users: number
  active_users_7d: number
  active_users_30d: number
  total_logins: number
  apps_with_users: number
}

export interface AppStats {
  app_id: string
  app_name: string
  owner_id: string
  total_users: number
  active_users_7d: number
  active_users_30d: number
  total_logins: number
}

interface UseUserStatsReturn {
  globalStats: GlobalStats | null
  appStats: AppStats[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useUserStats(): UseUserStatsReturn {
  const { user } = useAuth()
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null)
  const [appStats, setAppStats] = useState<AppStats[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadStats = useCallback(async () => {
    if (!user) {
      setGlobalStats(null)
      setAppStats([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Fetch global stats
      const { data: global, error: globalError } = await supabase
        .from('global_user_stats')
        .select('*')
        .single()

      if (globalError && globalError.code !== 'PGRST116') throw globalError

      setGlobalStats(global || {
        total_users: 0,
        active_users_7d: 0,
        active_users_30d: 0,
        total_logins: 0,
        apps_with_users: 0
      })

      // Fetch per-app stats
      const { data: apps, error: appsError } = await supabase
        .from('app_user_stats')
        .select('*')
        .eq('owner_id', user.id)
        .order('total_users', { ascending: false })

      if (appsError) throw appsError

      setAppStats((apps || []).map(app => ({
        app_id: app.app_id,
        app_name: app.app_name,
        owner_id: app.owner_id,
        total_users: parseInt(String(app.total_users ?? 0), 10),
        active_users_7d: parseInt(String(app.active_users_7d ?? 0), 10),
        active_users_30d: parseInt(String(app.active_users_30d ?? 0), 10),
        total_logins: parseInt(String(app.total_logins ?? 0), 10)
      })))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  return {
    globalStats,
    appStats,
    loading,
    error,
    refresh: loadStats
  }
}
